import React, { useEffect, useMemo, createContext, useContext } from "react";
import { useDetail } from "seed/gql";

const UserUsageContext = createContext(undefined);

export const useUserUsage = () => {
  const context = useContext(UserUsageContext);
  if(!context)
    throw new Error("useUserUsage must be used within a UserUsageProvider");
  return context;
}

export const UserUsageProvider = ({ children }) => {

  const userId = sessionStorage.getItem("id");
  const localUsage = localStorage.getItem("usage_limits");
  const parsedUser = localUsage ? JSON.parse(localUsage) : null;
  const isExpired = parsedUser ? ((new Date()).getTime() - (new Date(parsedUser.updatedAt)).getTime()) > 3600000 : true;
  const shouldFetch = !localUsage || isExpired;

  const reqLicense = useDetail(`{
    user {
      license {
        name
        fileStorage
        flows
        category {
          name
        }
      }
    }
  }`, userId, { skip: !shouldFetch });

  const reqResourcesUsed = useDetail(`{
    user {

      creditsTotal
      creditsUsed
      creditsAvailable
      
      storageUsed
      storageAvailable

      flowsUsed
      flowsAvailable

    }
  }`, userId, { skip: !shouldFetch });

  const userResourcesUsed = reqResourcesUsed.data?.user || {};
  const userLicense = reqLicense.data?.user || {};

  const user = useMemo(() => (
    localUsage != null
      ? JSON.parse(localUsage)
      : { ...userResourcesUsed, ...userLicense, updatedAt: new Date() }
  ), [localUsage, userResourcesUsed, userLicense]);

  useEffect(() => {
    if (shouldFetch && user.license?.name && userResourcesUsed?.creditsTotal)
      localStorage.setItem("usage_limits", JSON.stringify({ ...user, updatedAt: new Date() }));
  }, [shouldFetch, user]);

  const userUsage = {
    credits: {
      used: user?.creditsUsed || 0,
      total: user?.creditsTotal || 0,
      available: user?.creditsAvailable || 0,
      percentage: (user?.creditsUsed / user?.creditsTotal * 100) || 0,
    },
    storage: {
      used: user?.storageUsed || 0,
      total: user?.license?.fileStorage || 0, 
      available: user?.storageAvailable || 0,
      percentage: (user?.storageUsed / user?.license?.fileStorage * 100) || 0,
    },
    flows: {
      used: user?.flowsUsed || 0,
      total: user?.license?.flows || 0,
      available: user?.flowsAvailable || 0,
      percentage: (user?.flowsUsed / user?.license?.flows * 100) || 0,
    },
    license: user.license || {}
  }

  return (
    <UserUsageContext.Provider value={{ userUsage }}>
      {children}
    </UserUsageContext.Provider>
  );

}